import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Typography,
} from "@mui/material";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatJalali, formatMoney } from "../lib/format";
import { ReceiptDialog, type ReceiptTarget } from "../components/ReceiptDialog";
import { ContractDialog } from "../components/ContractDialog";
import { PrintableReceipt } from "../components/PrintableReceipt";

interface BookingRow {
  id: string;
  date: string;
  shift: string;
  status: string;
  guestCount: number;
  paidMinor: number;
  balanceMinor: number;
  contractId: string | null;
  quote: { totalMinor: number; currency: string; depositDueMinor: number };
}

const TABS = [
  { value: "pending_request", label: "درخواست‌ها" },
  { value: "pending_deposit", label: "در انتظار بیعانه" },
  { value: "confirmed", label: "تأیید شده" },
  { value: "completed", label: "برگزار شده" },
] as const;

const SHIFT_LABEL: Record<string, string> = {
  MORNING: "صبح",
  EVENING: "شب",
  FULL_DAY: "تمام روز",
};

export function BookingsPage() {
  const { orgId } = useSession();
  const [tab, setTab] = useState<string>("pending_request");
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [receiptTarget, setReceiptTarget] = useState<ReceiptTarget | null>(null);
  const [contractView, setContractView] = useState<string | null>(null);
  const [printPaymentId, setPrintPaymentId] = useState<string | null>(null);

  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, "bookings"),
      where("orgId", "==", orgId),
      where("status", "==", tab),
      orderBy("date"),
    );
    return onSnapshot(q, (snap) => {
      setRows(
        snap.docs.map((d) => {
          const data = d.data() as Omit<BookingRow, "id">;
          return { id: d.id, ...data, contractId: data.contractId ?? null };
        }),
      );
    });
  }, [orgId, tab]);

  async function confirmRequest(bookingId: string) {
    setBusyId(bookingId);
    try {
      await api(`/orgs/${orgId}/bookings/${bookingId}/confirm`, { method: "POST" });
      setToast("درخواست تأیید شد — مشتری برای پرداخت بیعانه خبر شد");
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusyId(null);
    }
  }

  async function issueContract(bookingId: string) {
    setBusyId(bookingId);
    try {
      const result = await api<{ contractId: string }>(
        `/orgs/${orgId}/bookings/${bookingId}/contract`,
        { method: "POST" },
      );
      setToast("قرارداد صادر شد — مشتری برای امضا خبر شد");
      setContractView(result.contractId);
    } catch (e) {
      setToast(e instanceof Error ? e.message : "خطا");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <Typography variant="h5" mb={2}>
        رزروها
      </Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      <Paper variant="outlined" sx={{ overflowX: "auto" }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>تاریخ</TableCell>
              <TableCell>وقت</TableCell>
              <TableCell>مهمانان</TableCell>
              <TableCell>مجموع</TableCell>
              <TableCell>پرداخت شده</TableCell>
              <TableCell>باقی‌مانده</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{formatJalali(row.date)}</TableCell>
                <TableCell>
                  <Chip size="small" label={SHIFT_LABEL[row.shift] ?? row.shift} />
                </TableCell>
                <TableCell>{row.guestCount}</TableCell>
                <TableCell>{formatMoney(row.quote.totalMinor, row.quote.currency)}</TableCell>
                <TableCell>{formatMoney(row.paidMinor, row.quote.currency)}</TableCell>
                <TableCell>{formatMoney(row.balanceMinor, row.quote.currency)}</TableCell>
                <TableCell align="left" sx={{ whiteSpace: "nowrap" }}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {row.status === "pending_request" && (
                      <Button
                        size="small"
                        variant="contained"
                        disabled={busyId === row.id}
                        onClick={() => confirmRequest(row.id)}
                      >
                        تأیید
                      </Button>
                    )}
                    {["pending_deposit", "confirmed"].includes(row.status) && (
                      <>
                        {row.balanceMinor > 0 && (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            onClick={() =>
                              setReceiptTarget({
                                bookingId: row.id,
                                balanceMinor: row.balanceMinor,
                                depositDueMinor: row.quote.depositDueMinor,
                                paidMinor: row.paidMinor,
                              })
                            }
                          >
                            ثبت رسید
                          </Button>
                        )}
                        {row.contractId ? (
                          <Button size="small" onClick={() => setContractView(row.contractId)}>
                            قرارداد
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            disabled={busyId === row.id}
                            onClick={() => issueContract(row.id)}
                          >
                            صدور قرارداد
                          </Button>
                        )}
                      </>
                    )}
                    {row.status === "completed" && row.contractId && (
                      <Button size="small" onClick={() => setContractView(row.contractId)}>
                        قرارداد
                      </Button>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography color="text.secondary" textAlign="center" py={3}>
                    موردی نیست
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {orgId && (
        <ReceiptDialog
          orgId={orgId}
          target={receiptTarget}
          onClose={() => setReceiptTarget(null)}
          onRecorded={({ receiptNo, paymentId }) => {
            setToast(`رسید ${receiptNo} ثبت شد — در صفحه مالی تأییدش کنید تا به دفاتر برود`);
            setPrintPaymentId(paymentId);
          }}
        />
      )}
      <ContractDialog contractId={contractView} onClose={() => setContractView(null)} />
      <PrintableReceipt paymentId={printPaymentId} onClose={() => setPrintPaymentId(null)} />

      <Snackbar open={!!toast} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} severity="info">
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}
